import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import ReportMonthSelect from '@/components/ReportMonthSelect'
import JournalEntryPreview from '@/components/JournalEntryPreview'
import WarningBanner from '@/components/WarningBanner'
import AccountingToolPage from '@/components/AccountingToolPage'
import { toast } from '@/store/toastStore'
import type { SelectOption } from '@/components/SearchableSelect'
import { HoverHelp } from '@/tableControls'
import { useQBOData } from '@/hooks/useQBOData'
import {
  createPrefEquityDraft,
  getPrefEquityHistory,
  type PrefEquityDraftResponse,
  type PrefEquityHistoryItem,
  type PrefEquitySourceData,
} from '@/services/prefEquity'
import {
  getQboIdMappings,
  getJournalEntryDraft,
  postJournalEntry,
  updateJournalEntryLines,
  type JournalEntryLine,
} from '@/services/accounting'
import { useMonthCloseNavStore } from '@/store/monthCloseNavStore'
import { countNonCompleteMonthOptions } from '@/utils/allocationFormat'
import { formatApiError } from '@/utils/apiError'
import { periodBoundsForMonth } from '@/utils/allocationFormat'
import {
  mergePrefEquityMonthSelectOptions,
  pickOldestNonCompleteMonth,
} from '@/pages/prefEquityMonthSelect'

function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const num = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(num)) return String(value)
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

type PrefEquityLineGroup = 'return' | 'principal' | 'ownership_change'

type PrefEquityLine = JournalEntryLine & { group?: PrefEquityLineGroup }

const PREF_EQUITY_LINE_GROUPS: Array<{
  id: PrefEquityLineGroup
  label: string
}> = [
  { id: 'return', label: 'Return' },
  { id: 'principal', label: 'Principal' },
  { id: 'ownership_change', label: 'Ownership change' },
]

function formatShareDisplay(share: number | string): string {
  const num = typeof share === 'string' ? Number(share) : share
  if (!Number.isFinite(num)) return String(share)
  if (num <= 1) return `${(num * 100).toFixed(4)}%`
  return String(num)
}

function memberTotalsForGroup(
  group: PrefEquityLineGroup,
  sourceData: PrefEquitySourceData,
): Array<[string, number]> {
  if (group === 'return' && sourceData.pref_return_by_member) {
    return Object.entries(sourceData.pref_return_by_member)
      .filter(([, amount]) => Number(amount) !== 0)
      .sort(([a], [b]) => a.localeCompare(b))
  }
  if (group === 'principal' && sourceData.principal_swept_by_member) {
    return Object.entries(sourceData.principal_swept_by_member)
      .filter(([, amount]) => Number(amount) !== 0)
      .sort(([a], [b]) => a.localeCompare(b))
  }
  if (group === 'ownership_change' && sourceData.ownership_transfers?.length) {
    const totals = new Map<string, number>()
    for (const transfer of sourceData.ownership_transfers) {
      for (const [member, delta] of Object.entries(transfer.deltas ?? {})) {
        const amount = Number(delta)
        if (amount === 0) continue
        totals.set(member, (totals.get(member) ?? 0) + amount)
      }
    }
    return [...totals.entries()].sort(([a], [b]) => a.localeCompare(b))
  }
  return []
}

function groupPrefEquityLines(lines: JournalEntryLine[]): Record<PrefEquityLineGroup, PrefEquityLine[]> {
  const grouped: Record<PrefEquityLineGroup, PrefEquityLine[]> = {
    return: [],
    principal: [],
    ownership_change: [],
  }
  for (const line of lines as PrefEquityLine[]) {
    const group = line.group ?? 'return'
    if (group in grouped) {
      grouped[group as PrefEquityLineGroup].push(line)
    }
  }
  return grouped
}

function groupTotals(groupLines: PrefEquityLine[]) {
  const debits = groupLines
    .filter((line) => line.posting_type === 'Debit')
    .reduce((sum, line) => sum + Number(line.amount), 0)
  const credits = groupLines
    .filter((line) => line.posting_type === 'Credit')
    .reduce((sum, line) => sum + Number(line.amount), 0)
  return { debits, credits }
}

function isSourceData(value: unknown): value is PrefEquitySourceData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'l_period_earned' in value &&
    'treatment' in value
  )
}

function historyItemToDraft(item: PrefEquityHistoryItem): PrefEquityDraftResponse {
  return {
    id: item.id,
    lines: item.lines,
    warnings: item.warnings ?? [],
    private_note: item.private_note ?? '',
    doc_number: item.doc_number,
    txn_date: item.txn_date,
    source_data: isSourceData(item.source_data)
      ? item.source_data
      : {
          month_end: item.txn_date,
          l_period_earned: 0,
          k_accrued_uncapitalized: 0,
          h_capitalized: null,
          is_capitalization_month: false,
          cap_date: null,
          opening_balance: 0,
          closing_balance: 0,
          economic_balance: 0,
          treatment: item.doc_number,
          ledger_rows: [],
        },
    total_debits: item.total_debits,
    total_credits: item.total_credits,
    status: item.status,
  }
}

export default function PreferredEquity() {
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null)
  const [draft, setDraft] = useState<PrefEquityDraftResponse | null>(null)
  const [lines, setLines] = useState<JournalEntryLine[]>([])
  const [history, setHistory] = useState<PrefEquityHistoryItem[]>([])
  const [historyReady, setHistoryReady] = useState(false)
  const [loading, setLoading] = useState(false)
  const [savingLines, setSavingLines] = useState(false)
  const [posting, setPosting] = useState(false)
  const [accountOptions, setAccountOptions] = useState<SelectOption[]>([])
  const draftIdRef = useRef<number | null>(null)

  const { locations } = useQBOData()
  const locationOptions: SelectOption[] = useMemo(
    () => locations.map((l) => ({ id: l.id, label: l.name })),
    [locations]
  )

  const sourceData = draft && isSourceData(draft.source_data) ? draft.source_data : null
  const draftStatus = draft?.status ?? 'draft'
  const qboUrl = history.find((item) => item.id === draft?.id)?.qbo_url ?? null

  const monthSelectOptions = useMemo(
    () => mergePrefEquityMonthSelectOptions(history.map((item) => item.txn_date)),
    [history],
  )
  const setPreferredEquityCount = useMonthCloseNavStore((s) => s.setPreferredEquityCount)

  useEffect(() => {
    if (!historyReady) return
    setPreferredEquityCount(countNonCompleteMonthOptions(monthSelectOptions))
  }, [historyReady, monthSelectOptions, setPreferredEquityCount])

  const groupedLines = useMemo(() => groupPrefEquityLines(lines), [lines])

  const nonEmptyGroups = useMemo(
    () => PREF_EQUITY_LINE_GROUPS.filter(({ id }) => groupedLines[id].length > 0),
    [groupedLines],
  )

  const prefReturnByMember = useMemo(() => {
    const byMember = sourceData?.pref_return_by_member
    if (!byMember) return []
    return Object.entries(byMember)
      .filter(([, amount]) => Number(amount) !== 0)
      .sort(([a], [b]) => a.localeCompare(b))
  }, [sourceData])

  const ownershipUsedRows = useMemo(() => {
    const schedule = sourceData?.preferred_ownership_used
    if (!schedule) return []
    const rows: Array<{ date: string; member: string; share: number | string }> = []
    for (const [date, byMember] of Object.entries(schedule)) {
      for (const [member, share] of Object.entries(byMember)) {
        rows.push({ date, member, share })
      }
    }
    return rows.sort(
      (a, b) => a.date.localeCompare(b.date) || a.member.localeCompare(b.member),
    )
  }, [sourceData])

  useEffect(() => {
    draftIdRef.current = draft?.id ?? null
  }, [draft?.id])

  const balanced = useMemo(() => {
    if (!draft) return false
    const debits = Number(draft.total_debits)
    const credits = Number(draft.total_credits)
    return Math.abs(debits - credits) < 0.005
  }, [draft])

  const missingLocations = useMemo(
    () => lines.some((l) => !l.department_id && !l.department_name?.trim()),
    [lines]
  )

  useEffect(() => {
    async function loadQboMappings() {
      try {
        const response = await getQboIdMappings()
        setAccountOptions(
          response.mappings
            .filter((mapping) => mapping.mapping_type === 'account')
            .map((mapping) => ({ id: mapping.qbo_id, label: mapping.display_name }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      } catch (err) {
        console.error('Failed to load QBO account mappings:', err)
      }
    }
    void loadQboMappings()
  }, [])

  async function refreshDraftFromApi(draftId: number) {
    const refreshed = await getJournalEntryDraft(draftId)
    setDraft((prev) => {
      if (!prev || prev.id !== draftId) return prev
      return {
        ...prev,
        status: refreshed.status,
        lines: refreshed.lines,
        total_debits: refreshed.total_debits,
        total_credits: refreshed.total_credits,
      }
    })
    setLines(refreshed.lines)
  }

  const loadHistory = useCallback(async () => {
    try {
      const data = await getPrefEquityHistory()
      setHistory(data.drafts)

      const currentDraftId = draftIdRef.current
      if (!currentDraftId) return

      const historyItem = data.drafts.find((item) => item.id === currentDraftId)
      if (!historyItem || historyItem.status !== 'posted') {
        try {
          await refreshDraftFromApi(currentDraftId)
        } catch (err) {
          console.error('Failed to refresh draft after history reconcile:', err)
        }
      }
    } catch (err) {
      console.error('Failed to load pref equity history:', err)
    } finally {
      setHistoryReady(true)
    }
  }, [])

  function handleMonthChange(month: string) {
    setSelectedMonth(month)
    const historyItem = history.find((item) => item.txn_date.slice(0, 7) === month)
    if (historyItem) {
      const nextDraft = historyItemToDraft(historyItem)
      setDraft(nextDraft)
      setLines(nextDraft.lines)
    } else {
      setDraft(null)
      setLines([])
    }
  }

  useEffect(() => {
    void loadHistory()
  }, [loadHistory])

  useEffect(() => {
    if (!historyReady) return

    const monthIds = monthSelectOptions.map((option) => option.id)
    if (monthIds.length === 0) {
      setSelectedMonth(null)
      return
    }

    setSelectedMonth((current) => {
      if (current && monthIds.includes(current)) return current
      return pickOldestNonCompleteMonth(monthSelectOptions)
    })
  }, [historyReady, monthSelectOptions])

  useEffect(() => {
    if (!selectedMonth) return
    const historyItem = history.find((item) => item.txn_date.slice(0, 7) === selectedMonth)
    if (historyItem && draft?.id !== historyItem.id) {
      const nextDraft = historyItemToDraft(historyItem)
      setDraft(nextDraft)
      setLines(nextDraft.lines)
    }
  }, [selectedMonth, history, draft?.id])

  useEffect(() => {
    if (!draft?.id || draftStatus === 'posted') return

    let cancelled = false
    void getJournalEntryDraft(draft.id)
      .then((refreshed) => {
        if (cancelled) return
        setDraft((prev) => (prev ? { ...prev, status: refreshed.status } : prev))
        if (refreshed.status === 'posted') {
          void loadHistory()
        }
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to refresh draft status:', err)
      })

    return () => {
      cancelled = true
    }
  }, [draft?.id, draftStatus, loadHistory])

  async function handleGenerate() {
    if (!selectedMonth) {
      toast.error('Select a month')
      return
    }
    const monthEndDate = periodBoundsForMonth(selectedMonth).periodEnd
    try {
      setLoading(true)
      const result = await createPrefEquityDraft(monthEndDate)
      setDraft({ ...result, status: result.status ?? 'draft' })
      setLines(result.lines)
      await loadHistory()
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to generate draft'))
    } finally {
      setLoading(false)
    }
  }

  async function handleLinesChange(next: JournalEntryLine[]) {
    setLines(next)
    if (!draft?.id) return
    try {
      setSavingLines(true)
      const updated = await updateJournalEntryLines(draft.id, next)
      setLines(updated.lines)
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              lines: updated.lines,
              total_debits: updated.total_debits,
              total_credits: updated.total_credits,
            }
          : prev
      )
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save line changes'))
    } finally {
      setSavingLines(false)
    }
  }

  async function handleGroupLinesChange(
    group: PrefEquityLineGroup,
    nextGroupLines: JournalEntryLine[],
  ) {
    const stamped = nextGroupLines.map((line) => ({
      ...line,
      group,
    })) as PrefEquityLine[]
    const byGroup = groupPrefEquityLines(lines)
    byGroup[group] = stamped
    const merged = PREF_EQUITY_LINE_GROUPS.flatMap(({ id }) => byGroup[id])
    await handleLinesChange(merged)
  }

  async function handlePost() {
    if (!draft?.id) return
    try {
      setPosting(true)
      const result = await postJournalEntry(draft.id)
      setDraft({
        ...result.draft,
        warnings: result.draft.warnings ?? [],
        private_note: result.draft.private_note ?? '',
        source_data: isSourceData(result.draft.source_data)
          ? result.draft.source_data
          : draft.source_data,
      })
      setLines(result.draft.lines)
      await loadHistory()
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to post journal entry'))
    } finally {
      setPosting(false)
    }
  }

  return (
    <AccountingToolPage
      title="Preferred Equity"
      description="Generate month-end preferred return journal entries from the corrected HP Threshold ledger"
    >
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="container-title">Generate draft</h2>
        <p className="mt-1 text-xs text-gray-500">
          Post-conversion only (on or after 2025-04-01).
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="pref-equity-month" className="mb-1.5 block text-xs font-medium text-gray-600">
              <HoverHelp label="Month" aria-label="Preferred equity month help">
                Months run from April 2025 through the last completed calendar month.
              </HoverHelp>
            </label>
            <ReportMonthSelect
              id="pref-equity-month"
              options={monthSelectOptions}
              value={selectedMonth || null}
              onChange={(id) => id && handleMonthChange(id)}
              disabled={!monthSelectOptions.length}
              placeholder="Select month"
              className="w-[300px] max-w-full text-sm"
            />
          </div>
          <Button
            type="button"
            gradient
            size="lg"
            onClick={() => void handleGenerate()}
            disabled={loading || !selectedMonth}
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {loading
              ? draftStatus === 'posted'
                ? 'Regenerating…'
                : 'Generating…'
              : draftStatus === 'posted'
                ? 'Regenerate draft'
                : 'Generate draft'}
          </Button>
        </div>
      </div>

      {draft && sourceData ? (
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div
            className={`flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4 ${
              draftStatus === 'posted'
                ? 'border-emerald-200 bg-emerald-50/70'
                : 'border-gray-200'
            }`}
          >
            <div>
              <h2 className="text-base font-semibold text-gray-900">{sourceData.treatment}</h2>
              <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <code
                  className={`rounded px-1.5 py-0.5 font-mono text-[11px] ${
                    draftStatus === 'posted'
                      ? 'bg-emerald-100/80 text-emerald-900'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {draft.doc_number}
                </code>
                <span>Txn date {draft.txn_date}</span>
                {draftStatus === 'posted' && qboUrl ? (
                  <a
                    href={qboUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-0.5 font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
                  >
                    View in QuickBooks
                    <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                  </a>
                ) : null}
              </p>
            </div>
            {draftStatus === 'posted' ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200">
                <CheckCircleIcon className="h-4 w-4" />
                Posted to QuickBooks
              </span>
            ) : draft.id ? (
              <button
                type="button"
                className="btn btn-primary text-sm"
                onClick={() => void handlePost()}
                disabled={posting || loading || !balanced || missingLocations || savingLines}
              >
                {posting ? 'Posting…' : 'Post to QuickBooks'}
              </button>
            ) : null}
          </div>

          <div className="space-y-4 p-5">
            <div className="rounded-lg bg-gray-50/90 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Ledger inputs
              </h3>
              <div className="mt-3 space-y-2">
                <div className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 text-sm">
                  <span className="text-gray-500">Period preferred return earned</span>
                  <span className="font-mono tabular-nums text-gray-900">
                    {formatMoney(sourceData.l_period_earned)}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 text-sm">
                  <span className="text-gray-500">Accrued uncapitalized (month-end)</span>
                  <span className="font-mono tabular-nums text-gray-900">
                    {formatMoney(sourceData.k_accrued_uncapitalized)}
                  </span>
                </div>
                {sourceData.is_capitalization_month && sourceData.h_capitalized !== null ? (
                  <div className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 text-sm">
                    <span className="text-gray-500">Quarter capitalized preferred return</span>
                    <span className="font-mono tabular-nums text-gray-900">
                      {formatMoney(sourceData.h_capitalized)}
                    </span>
                  </div>
                ) : null}
                {sourceData.cap_date ? (
                  <div className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 text-sm">
                    <span className="text-gray-500">Capitalization date</span>
                    <span className="text-gray-900">{sourceData.cap_date}</span>
                  </div>
                ) : null}
                <div className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 text-sm">
                  <span className="text-gray-500">Opening balance (month-end)</span>
                  <span className="font-mono tabular-nums text-gray-900">
                    {formatMoney(sourceData.opening_balance)}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 text-sm">
                  <span className="text-gray-500">Closing balance (month-end)</span>
                  <span className="font-mono tabular-nums text-gray-900">
                    {formatMoney(sourceData.closing_balance)}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 border-t border-gray-200/80 pt-2 text-sm font-medium">
                  <span className="text-gray-600">Economic balance</span>
                  <span className="font-mono tabular-nums text-gray-900">
                    {formatMoney(sourceData.economic_balance)}
                  </span>
                </div>
              </div>
              <p className="mt-3 text-xs text-gray-500">
                {sourceData.ledger_rows.length} ledger row
                {sourceData.ledger_rows.length === 1 ? '' : 's'} included in the HP Threshold
                attachment.
              </p>
            </div>

            {prefReturnByMember.length > 0 ? (
              <div className="rounded-lg bg-gray-50/90 p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Preferred return by member
                </h3>
                <div className="mt-3 space-y-2">
                  {prefReturnByMember.map(([member, amount]) => (
                    <div
                      key={member}
                      className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 text-sm"
                    >
                      <span className="text-gray-500">{member}</span>
                      <span className="font-mono tabular-nums text-gray-900">
                        {formatMoney(amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {ownershipUsedRows.length > 0 ? (
              <div className="rounded-lg bg-gray-50/90 p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Ownership schedule used
                </h3>
                <div className="table-scroll-mobile mt-3 overflow-x-auto rounded-lg border border-gray-200">
                  <table className="table-forecast min-w-full">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Member</th>
                        <th className="text-right">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ownershipUsedRows.map((row) => (
                        <tr key={`${row.date}-${row.member}`}>
                          <td>{row.date}</td>
                          <td>{row.member}</td>
                          <td className="tabular-nums text-right">
                            {formatShareDisplay(row.share)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            {draft.warnings.map((w, i) => (
              <WarningBanner key={`${w}-${i}`} message={w} />
            ))}
            {missingLocations ? (
              <WarningBanner message="Assign a location on every line before posting." />
            ) : null}

            {nonEmptyGroups.map(({ id, label }) => {
              const groupLines = groupedLines[id]
              const totals = groupTotals(groupLines)
              const memberTotals = sourceData ? memberTotalsForGroup(id, sourceData) : []
              return (
                <div key={id} className="space-y-3">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <h3 className="text-sm font-semibold text-gray-800">{label}</h3>
                    {memberTotals.length > 0 ? (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                        {memberTotals.map(([member, amount]) => (
                          <span key={`${id}-${member}`}>
                            {member}:{' '}
                            <span className="font-mono tabular-nums text-gray-900">
                              {formatMoney(amount)}
                            </span>
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <JournalEntryPreview
                    lines={groupLines}
                    totalDebits={totals.debits}
                    totalCredits={totals.credits}
                    variant="split"
                    hideHeader
                    editableAmounts
                    editableLines
                    editableLocations
                    accountOptions={accountOptions}
                    locationOptions={locationOptions}
                    onLinesChange={(next) => void handleGroupLinesChange(id, next)}
                  />
                </div>
              )
            })}
            {savingLines ? (
              <p className="text-xs text-gray-500">Saving line changes…</p>
            ) : null}

            {draft.private_note ? (
              <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                <span className="font-medium text-gray-800">Memo:</span>{' '}
                {draft.private_note}
              </div>
            ) : null}
          </div>
        </section>
      ) : null}
    </AccountingToolPage>
  )
}
